/**
 * Carries out the help assistant's actions (D10-ACT-001..004, #3605, #3606).
 *
 * Registers the panel's action handler with the chat context, which calls it
 * once a reply carrying a validated action has completed.
 *
 *  - `start_tour` re-checks the tour against the registry and the live gate
 *    (`checkTourLaunch`), closes the panel -- on mobile it is a bottom sheet
 *    that would cover the tour's first target -- and starts it through
 *    `TourContext.startTour`, so progress is recorded like any other launch.
 *    A tour that cannot run here is refused with a note, never started. When
 *    the tour ends the panel comes back with the conversation (#3624).
 *  - `open_feedback` fetches the server-built, scrubbed and fenced transcript
 *    excerpt (D10-SEC-006), closes the panel and opens the existing feedback
 *    form with the type preselected and the description pre-filled. The user
 *    still reviews it and presses Send (D10-SEC-004); nothing is submitted here.
 *  - `navigate` re-checks the route against the user's live allowed pages (exact
 *    match only) and opens it with the SPA router. On a phone the panel is a
 *    bottom sheet, so it closes to show the page; on a desktop it stays open.
 *  - `highlight` re-checks the route and the anchor catalogue (a record-page
 *    control only on the record page showing now, #3633), closes the panel,
 *    opens the page and shows the tour tooltip on the control through
 *    `TourContext.showHint` (no tour progress is recorded). When the hint is
 *    closed the panel comes back with the conversation. A control that is not
 *    on the page shows nothing; the panel comes back with a short note.
 *
 * Every action waits a beat so the reply can be read first. If "New chat" is
 * pressed in that beat, the action belongs to a conversation that no longer
 * exists and is dropped (`ctx.isStale`, #3702).
 */
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSupportExcerpt,
  isAllowedHighlight,
  isAllowedSupportRoute,
  type SupportAction,
} from "../../api/support";
import { useSupportChat, type SupportActionContext } from "../../contexts/supportChat";
import { useTour } from "../../contexts/tour";
import { openFeedback } from "../../lib/feedback/openFeedback";
import {
  buildFeedbackDescription,
  checkTourLaunch,
  tourRefusalText,
} from "../../lib/support/actions";
import { useTourGate } from "../../lib/tours/useTourGate";
import { useSupportLists } from "./useSupportLists";

/** A beat to read the reply before the panel steps aside for the action. */
export const ACTION_PAUSE_MS = 900;

const pause = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/** Neutral fallback if the status payload has no assistant name. */
const FALLBACK_NAME = "Help";

export const NOT_ALLOWED_NOTE = "That page isn't available to you.";
export const HINT_MISSING_NOTE =
  "I couldn't find that control on the page, so there's nothing to point at here.";
export const HINT_BUSY_NOTE = "A guided tour is running. Finish it, then ask again.";

/** The panel is a bottom sheet below Tailwind's `sm` breakpoint. */
function isBottomSheet(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return !window.matchMedia("(min-width: 640px)").matches;
}

export function useSupportActions(): void {
  const { setOpen, setActionHandler, status } = useSupportChat();
  const { startTour, showHint } = useTour();
  const gate = useTourGate();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { allowedRoutes } = useSupportLists();
  const assistantName = status?.assistant_name?.trim() || FALLBACK_NAME;

  const run = useCallback(
    async (action: SupportAction, ctx: SupportActionContext): Promise<string | void> => {
      if (action.name === "start_tour") {
        const check = checkTourLaunch(action.args.tour_id, gate, pathname);
        if (check !== "ok") return tourRefusalText(check);
        await pause(ACTION_PAUSE_MS);
        if (ctx.isStale?.()) return;
        setOpen(false);
        // Let the panel unmount before the tour engine measures its first anchor.
        // When the tour ends, however it ends, the panel comes back with the
        // conversation (#3624), as it does after a highlight.
        window.setTimeout(
          () => startTour(action.args.tour_id, { onEnd: () => setOpen(true) }),
          0,
        );
        return;
      }
      if (action.name === "navigate") {
        const { route } = action.args;
        // Exact membership of the live allowed list -- never a free-form URL.
        if (!isAllowedSupportRoute(route, allowedRoutes)) return NOT_ALLOWED_NOTE;
        await pause(ACTION_PAUSE_MS);
        if (ctx.isStale?.()) return;
        if (isBottomSheet()) setOpen(false);
        if (route !== pathname) navigate(route);
        return;
      }
      if (action.name === "highlight") {
        const { anchor, route, text } = action.args;
        // A record-page control only on the record page showing right now
        // (#3633): the route must be this page, never one to open.
        if (!isAllowedHighlight(anchor, route, allowedRoutes, pathname)) {
          return NOT_ALLOWED_NOTE;
        }
        await pause(ACTION_PAUSE_MS);
        if (ctx.isStale?.()) return;
        // Step aside so the page and the tooltip are visible; come back after.
        setOpen(false);
        const result = await showHint(
          { route, target: anchor, title: assistantName, body: text },
          { onEnd: () => setOpen(true) },
        );
        if (result === "shown") return;
        setOpen(true);
        return result === "busy" ? HINT_BUSY_NOTE : HINT_MISSING_NOTE;
      }
      // open_feedback
      let excerpt = "";
      if (ctx.conversationId) {
        try {
          excerpt = await getSupportExcerpt(ctx.conversationId);
        } catch {
          // The summary alone still makes a useful report.
          excerpt = "";
        }
      }
      await pause(ACTION_PAUSE_MS);
      // "New chat" meanwhile: the old reply's report is not opened (#3702).
      if (ctx.isStale?.()) return;
      setOpen(false);
      const description = buildFeedbackDescription(action.args.summary, excerpt);
      window.setTimeout(() => openFeedback({ type: action.args.type, description }), 0);
    },
    [gate, pathname, setOpen, startTour, allowedRoutes, navigate, showHint, assistantName],
  );

  useEffect(() => {
    setActionHandler(run);
    return () => setActionHandler(null);
  }, [run, setActionHandler]);
}
